import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '@shared/components/Button';
import SectionHeading from '@shared/components/SectionHeading';
import { DataTable, type DataTableColumn } from '@shared';
import { useIsMobile } from '@shared/contexts/ViewportContext';
import { sectionBoxP4 } from '@shared/lib/modalClasses';
import AlarmActions from '@/components/AlarmActions';
import AlarmNotes from '@/components/AlarmNotes';
import AlarmStateBadge from '@/components/AlarmStateBadge';
import EventLogTable from '@/components/EventLogTable';
import EventSeverityBadge from '@/components/EventSeverityBadge';
import Fact from '@/components/Fact';
import StatusBadge from '@/components/StatusBadge';
import {
  DELIVERY_STATUS_BADGE,
  DELIVERY_STATUS_LABELS,
  deliveryKindLabel,
  fmtDateTime,
  resolveReasonLabel,
} from '@/lib/alarmLabels';
import { isInternalLink, isSentryUrl } from '@/lib/internalLink';
import { alertError, linkAccentClass } from '@/lib/uiClasses';
import {
  getMonitorAlarm,
  getMonitorAlarmEvents,
  type AlarmUserRef,
  type MonitorAlarm,
  type MonitorAlarmDelivery,
  type MonitorAlarmDetail,
  type MonitorEventRow,
} from '@/services/api';

const EVENTS_PAGE = 20;
const muted = 'text-text-sub dark:text-slate-400';

const userLabel = (u: AlarmUserRef | null) => (u ? u.name || u.email : '');

function autoResolveLabel(min: number | null): string {
  if (min == null) return 'A mano';
  if (min % 1440 === 0) return `Tras ${min / 1440} ${min === 1440 ? 'día' : 'días'} sin repetirse`;
  if (min % 60 === 0) return `Tras ${min / 60} ${min === 60 ? 'hora' : 'horas'} sin repetirse`;
  return `Tras ${min} min sin repetirse`;
}

/** Lo que los detectores dejan para leer (§«Detector metadata keys»). */
function MetadataDetail({ metadata }: { metadata: Record<string, unknown> }) {
  const error = typeof metadata.error === 'string' ? metadata.error : '';
  const plan = typeof metadata.plan_name === 'string' ? metadata.plan_name : '';
  const models = Array.isArray(metadata.models) ? metadata.models.map(String) : [];
  const hasAny = Object.keys(metadata).length > 0;
  if (!hasAny) return null;
  return (
    <div className="space-y-2">
      {plan ? <p className="text-sm"><span className={muted}>Plan:</span> {plan}</p> : null}
      {models.length > 0 ? (
        <p className="text-sm break-words"><span className={muted}>Modelos:</span> {models.join(', ')}</p>
      ) : null}
      {error ? (
        <pre className="text-xs font-mono whitespace-pre-wrap break-words rounded-lg border border-border-subtle dark:border-border-subtle-dark p-3 max-h-64 overflow-auto">
          {error}
        </pre>
      ) : null}
      <details>
        <summary className={`text-xs cursor-pointer ${muted}`}>Datos técnicos (JSON)</summary>
        <pre className="mt-2 text-xs font-mono whitespace-pre-wrap break-words rounded-lg border border-border-subtle dark:border-border-subtle-dark p-3 max-h-64 overflow-auto">
          {JSON.stringify(metadata, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function DeliveriesTable({ rows }: { rows: MonitorAlarmDelivery[] }) {
  const columns: DataTableColumn<MonitorAlarmDelivery>[] = [
    { key: 'date', header: 'Fecha', className: `text-xs whitespace-nowrap ${muted}`, render: (d) => fmtDateTime(d.sent_at || d.created_at) },
    { key: 'channel', header: 'Canal', className: 'text-xs', render: (d) => d.channel_name || `#${d.channel_id}` },
    { key: 'kind', header: 'Tipo', className: 'text-xs whitespace-nowrap', render: (d) => deliveryKindLabel(d.kind) },
    {
      key: 'status',
      header: 'Estado',
      render: (d) => (
        <StatusBadge
          status={(DELIVERY_STATUS_BADGE as Record<string, string>)[d.status] ?? d.status}
          label={(DELIVERY_STATUS_LABELS as Record<string, string>)[d.status] ?? d.status}
        />
      ),
    },
    {
      key: 'error',
      header: 'Motivo',
      className: `text-xs max-w-[280px] truncate ${muted}`,
      render: (d) => (d.last_error ? <span title={d.last_error}>{d.last_error}</span> : '—'),
    },
  ];
  return (
    <DataTable<MonitorAlarmDelivery>
      data={rows}
      columns={columns}
      rowKey={(d) => d.id}
      emptyIcon="notifications_off"
      emptyLabel="Ningún aviso enviado por esta alarma"
    />
  );
}

interface Props {
  alarmId: number;
  /** Tras cualquier acción, con la alarma nueva (la lista de fuera la refresca). */
  onChanged?: (alarm: MonitorAlarm) => void;
  /** Al cargar (el modal pone el título). */
  onLoaded?: (alarm: MonitorAlarmDetail) => void;
  layout: 'modal' | 'page';
  /** Para cerrar el modal al seguir un enlace. */
  onNavigate?: () => void;
}

/**
 * EL detalle de una alarma: el modal (`AlarmDetailModal`) y la página
 * (`/monitor/alarmas/:id`) pintan este mismo componente, así que lo que se
 * añada aquí sale en los dos. En móvil, en la página, los datos se apilan y las
 * acciones van en una barra fija abajo.
 */
export default function AlarmDetail({ alarmId, onChanged, onLoaded, layout, onNavigate }: Props) {
  const isMobile = useIsMobile();
  const [detail, setDetail] = useState<MonitorAlarmDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  const [events, setEvents] = useState<{ rows: MonitorEventRow[]; total: number; offset: number }>({ rows: [], total: 0, offset: 0 });
  const [eventsLoading, setEventsLoading] = useState(false);

  // En una ref: el padre pasa una función nueva en cada render y eso no debe
  // volver a pedir el detalle.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getMonitorAlarm(alarmId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setEvents({ rows: d.events, total: d.events_total, offset: 0 });
        onLoadedRef.current?.(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'No se pudo cargar la alarma');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [alarmId, tick]);

  const changeEventsPage = (offset: number) => {
    setEventsLoading(true);
    getMonitorAlarmEvents(alarmId, EVENTS_PAGE, offset)
      .then((d) => setEvents({ rows: d.items, total: d.total, offset }))
      .catch(() => { /* la página anterior se queda */ })
      .finally(() => setEventsLoading(false));
  };

  // Una acción devuelve la cabecera nueva; notas, eventos y avisos pueden haber
  // cambiado también, así que se vuelve a pedir el detalle entero.
  const handleChanged = (alarm: MonitorAlarm) => {
    setDetail((prev) => (prev ? { ...prev, ...alarm } : prev));
    setTick((n) => n + 1);
    onChanged?.(alarm);
  };

  if (loading && !detail) {
    return <p className={`py-8 text-center ${muted}`}>Cargando alarma…</p>;
  }
  if (error && !detail) {
    return (
      <div className={`${alertError} flex flex-wrap items-center justify-between gap-2`}>
        <span>{error}</span>
        <Button type="button" variant="ghost" size="sm" icon="refresh" onClick={() => setTick((n) => n + 1)}>
          Reintentar
        </Button>
      </div>
    );
  }
  if (!detail) return null;

  const a = detail;
  const originLink = isInternalLink(a.link) ? a.link : null;
  const sentryLink = isSentryUrl(a.sentry_issue_url) ? a.sentry_issue_url : null;
  const bottomBar = layout === 'page' && isMobile;
  const actionsNode = (
    <AlarmActions alarm={a} actions={a.actions} onChanged={handleChanged} layout={bottomBar ? 'bar' : 'inline'} />
  );
  const hasActions = Object.entries(a.actions).some(([k, v]) => k !== 'note' && v);

  return (
    <div className={`space-y-4 ${bottomBar && hasActions ? 'pb-4' : ''}`}>
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <EventSeverityBadge severity={a.severity} />
          <AlarmStateBadge state={a.state} silencedUntil={a.silenced_until} />
          <span className={`text-xs ${muted}`}>{a.kind_label || a.kind} · {a.category_label}</span>
        </div>
        {layout === 'page' ? (
          <h2 className="text-lg font-bold text-text-main dark:text-white break-words">{a.title}</h2>
        ) : null}
        <p className={`text-[11px] font-mono break-all ${muted}`}>{a.alarm_key}</p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          {a.workshop_id != null ? (
            <Link to={`/talleres/${a.workshop_id}`} onClick={onNavigate} className={linkAccentClass}>
              {a.workshop_name || a.workshop_slug || `Taller #${a.workshop_id}`}
            </Link>
          ) : null}
          {originLink ? (
            <Link to={originLink} onClick={onNavigate} className={linkAccentClass}>Ir al origen →</Link>
          ) : null}
          {sentryLink ? (
            <a href={sentryLink} target="_blank" rel="noopener noreferrer" className={linkAccentClass}>Ver en Sentry →</a>
          ) : null}
        </div>
      </div>

      {!bottomBar && hasActions ? <div>{actionsNode}</div> : null}

      <section className={sectionBoxP4}>
        <dl className={`grid gap-4 ${bottomBar ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-4'}`}>
          <Fact label="Primera vez">{fmtDateTime(a.first_seen_at)}</Fact>
          <Fact label="Última vez">{fmtDateTime(a.last_seen_at)}</Fact>
          <Fact label="Veces">{a.count}</Fact>
          <Fact label="Reaperturas">{a.reopened_count}</Fact>
          <Fact label="Reconocida">
            {a.acknowledged_at ? `${fmtDateTime(a.acknowledged_at)}${a.acknowledged_by ? ` · ${userLabel(a.acknowledged_by)}` : ''}` : '—'}
          </Fact>
          <Fact label="Resuelta">
            {a.resolved_at
              ? `${fmtDateTime(a.resolved_at)} · ${resolveReasonLabel(a.resolve_reason)}${a.resolved_by ? ` · ${userLabel(a.resolved_by)}` : ''}`
              : '—'}
          </Fact>
          <Fact label="Silenciada hasta">
            {a.silenced_until ? `${fmtDateTime(a.silenced_until)}${a.silenced_by ? ` · ${userLabel(a.silenced_by)}` : ''}` : '—'}
          </Fact>
          <Fact label="Se resuelve sola">{autoResolveLabel(a.auto_resolve_after_min)}</Fact>
          <Fact label="Asignada">{userLabel(a.assigned_to) || '—'}</Fact>
          <Fact label="Tipo de evento">{a.event_label || a.event_type}</Fact>
        </dl>
      </section>

      {Object.keys(a.metadata ?? {}).length > 0 ? (
        <section className="space-y-2">
          <SectionHeading icon="info">Detalle</SectionHeading>
          <MetadataDetail metadata={a.metadata} />
        </section>
      ) : null}

      <section className="space-y-2">
        <SectionHeading icon="sticky_note_2">Notas</SectionHeading>
        <AlarmNotes
          alarmId={a.id}
          notes={a.notes}
          canAdd={a.actions.note}
          onAdded={(alarm) => handleChanged(alarm)}
        />
      </section>

      <section className="space-y-2">
        <SectionHeading icon="history">Eventos ({events.total})</SectionHeading>
        <EventLogTable
          rows={events.rows}
          total={events.total}
          offset={events.offset}
          pageSize={EVENTS_PAGE}
          onOffsetChange={changeEventsPage}
          loading={eventsLoading}
          showWorkshop={a.workshop_id == null}
          emptyLabel="Sin eventos"
        />
      </section>

      <section className="space-y-2">
        <SectionHeading icon="notifications">Entregas</SectionHeading>
        <DeliveriesTable rows={a.deliveries} />
      </section>

      {bottomBar && hasActions ? (
        <div className="sticky bottom-0 -mx-4 px-4 py-3 bg-white dark:bg-surface-dark border-t border-border-subtle dark:border-border-subtle-dark">
          {actionsNode}
        </div>
      ) : null}
    </div>
  );
}
